import { isAllocationDistributionTitle } from './allocation';
import { isExtraRankTable, rankTableOfTitle, type RankTable } from './ranked';
import { BOX_NAMES } from './scale';

/**
 * Which sheet of an Excel export holds the data, and what a summary table is (M4S-5592).
 *
 * The export writes a cover sheet ("Summary"), the data, and a sheet of net and mean rows. That
 * last sheet used to be called "Summary Tables" and sat second, ahead of the data. Both readers
 * of an export took the wrong sheet because of it: the iOOH report builder read `worksheets[1]`,
 * and DANI's `excelToCrosstabData` read every sheet in order and kept the first table it met for a
 * question. The sheet is now "Nets & Means" and comes last, and the readers choose by name, so
 * exports made before the change, which are still circulating, read correctly too.
 *
 * The same file owns the other half of the ticket: a matrix's Top 2 / Bottom 2 summary tables. A
 * summary table shares its question's code, so anything that finds "the table for question X" by
 * code must leave it out: in memory through `withoutSummaryBlocks`, and in a file through
 * `summaryBoxOfTitle`. Any new reader keyed by question code does the same.
 *
 * Pure: the workbook writer and the file readers import their names from here, so the writer and
 * the readers cannot drift apart. No exceljs and no app types (`src/lib` never imports the app's
 * `data.ts`, so blocks are typed structurally).
 */

/** The sheet of net and mean rows. Written last. A veto of the name is a change to this line. */
export const NETS_AND_MEANS_SHEET = 'Nets & Means';

/**
 * Sheets that are never the data, spelled as the export writes them: the cover, the net-and-mean
 * sheet under its old name, and under its new one. Used by the iOOH report builder's sheet choice.
 */
export const NON_DATA_SHEET_NAMES: ReadonlySet<string> = new Set([
    'Summary',
    'Summary Tables',
    NETS_AND_MEANS_SHEET,
]);

/**
 * The net-and-mean sheet under either name, lowercase because `excelToCrosstabData` compares
 * lowercased names. It never holds "summary": that cover sheet's "Respondents (n)" row is the only
 * place `totalRespondents` comes from, since a bare "Total" row does not match the base pattern.
 */
export const NETS_SHEET_NAMES: readonly string[] = [
    'summary tables',
    NETS_AND_MEANS_SHEET.toLowerCase(),
];

export function isNetsSheetName(name: string): boolean {
    return NETS_SHEET_NAMES.includes(name.trim().toLowerCase());
}

/**
 * Sheet names `excelToCrosstabData` takes for an index of the workbook and skips, lowercase. A
 * file's own sheet with one of these names is never read as data.
 */
export const INDEX_SHEET_NAMES: readonly string[] = ['toc', 'table of contents', 'index'];

export function isIndexSheetName(name: string): boolean {
    return INDEX_SHEET_NAMES.includes(name.trim().toLowerCase());
}

/**
 * M4S-5777 (X104): the linked table of contents the house workbook writes. Its name is one of
 * `INDEX_SHEET_NAMES`, so every reader already skips it, and `dataSheetIndex` never takes it.
 */
export const CONTENTS_SHEET = 'Table of Contents';

/**
 * M4S-5777 (X112): the optional sheet of open-end verbatims. Never the data: every reader skips it
 * (`isVerbatimSheetName`), and the writer reserves the name so no question's tab can take it. Two
 * words, not "OE": a question coded OE has always had a tab of that name, and a reader must keep
 * reading those files.
 */
export const VERBATIM_SHEET = 'OE Verbatims';

export function isVerbatimSheetName(name: string): boolean {
    return name.trim().toLowerCase() === VERBATIM_SHEET.toLowerCase();
}

/**
 * M4S-5777 (X104): what every table's footnote row starts with. A footnote row sits under a
 * question's box and holds text in column A only; a reader that walks a sheet row by row skips it
 * (`isFootnoteText`) so a note is never read as an answer. The writer and the readers import it
 * from here, in the same edit, so they cannot drift apart.
 */
export const FOOTNOTE_PREFIX = 'Table notes: ';

export function isFootnoteText(text: unknown): boolean {
    return typeof text === 'string' && text.trimStart().startsWith(FOOTNOTE_PREFIX.trim());
}

/**
 * Every name a data sheet or a question's tab must not take, lowercase because Excel compares
 * sheet names without regard to case: the cover, the net-and-mean sheet under either name, and
 * the index names. A sheet with one of these names is skipped by a reader, so the export gives
 * such a crosstab or variable a numbered name instead ("Summary Tables (2)"). It is built from
 * the sets above, so a name added to a reader is reserved in the writer in the same edit.
 */
export const RESERVED_SHEET_NAMES: readonly string[] = [
    ...[...NON_DATA_SHEET_NAMES].map((n) => n.toLowerCase()),
    ...INDEX_SHEET_NAMES,
    VERBATIM_SHEET.toLowerCase(),
];

/**
 * The index of the data sheet among a workbook's sheet names.
 *
 * "Total" wins wherever it sits (the legacy tool's crosstab). Otherwise the first sheet, from the
 * second on, whose name is not a cover, a nets sheet or a summary tab: the flat export names its
 * data sheet after the crosstab, and the one-tab-per-question export starts with the first
 * question's tab, which is a summary tab when the first question is a matrix with summary tables.
 * Failing both, the first sheet.
 */
export function dataSheetIndex(names: readonly string[]): number {
    const total = names.indexOf('Total');
    if (total >= 0) return total;
    for (let i = 1; i < names.length; i++) {
        if (
            !NON_DATA_SHEET_NAMES.has(names[i]) &&
            !isSummaryTabName(names[i]) &&
            // M4S-5777 (X104, X112): the table of contents and the verbatims are never the data.
            !isIndexSheetName(names[i]) &&
            !isVerbatimSheetName(names[i]) &&
            // M4S-5777 (X90): the unweighted set is never the data a reader takes.
            !isUnweightedSheetName(names[i])
        )
            return i;
    }
    return 0;
}

// ── Summary tables ────────────────────────────────────────────────────────────

/** Which box a summary table counts. The same values as a box row's `netType`. */
export type SummaryBox = 'top' | 'bottom';

/** Top 2 first: the order the box tables sit in, above the statement tables. */
export const SUMMARY_BOXES: readonly SummaryBox[] = ['top', 'bottom'];

/**
 * What a summary table counts: a box, or the statements' Mean (`mean`, M4S-5777 X60). The values of
 * `Block.summary`.
 */
export type SummaryKind = SummaryBox | 'mean';

/** Every summary in the order the tables sit: Top 2, Bottom 2, then the Mean. */
export const SUMMARY_KINDS: readonly SummaryKind[] = [...SUMMARY_BOXES, 'mean'];

/** What the Mean summary is called; a Mean is not a box. */
const MEAN_SUMMARY_NAME = 'Mean';

/**
 * What a summary table's title ends with: the question's text, then this. It is Juhi's wording
 * (her Tables 18 and 19), and it is built from the box names so a rename cannot leave the title
 * and the box rows saying different things.
 */
export const SUMMARY_TITLE_SUFFIX: Record<SummaryKind, string> = {
    top: ` - ${BOX_NAMES.top} Summary`,
    bottom: ` - ${BOX_NAMES.bottom} Summary`,
    mean: ` - ${MEAN_SUMMARY_NAME} Summary`,
};

/** "Top 2 Box Summary": the box name first, for a header that has to survive being cut short. */
export function summaryLeadLabel(box: SummaryKind): string {
    return `${box === 'mean' ? MEAN_SUMMARY_NAME : BOX_NAMES[box]} Summary`;
}

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** A dash of any width with a space before it, then a box's summary label, at the end of a header. */
const summaryTitleRe = (box: SummaryKind) =>
    new RegExp(`\\s[-–—]+\\s*${escapeRegExp(summaryLeadLabel(box))}\\s*$`, 'i');
const SUMMARY_TITLE_RE: Record<SummaryKind, RegExp> = {
    top: summaryTitleRe('top'),
    bottom: summaryTitleRe('bottom'),
    mean: summaryTitleRe('mean'),
};

/**
 * The box a table header says it summarises, or null when it is an ordinary table. It looks at the
 * end of the header only, in either form the readers meet: "Table 18. QCUSTOM2. <question> - Top 2
 * Box Summary" (a legacy SOTG file) and "QCUSTOM2 — <question> - Top 2 Box Summary" (Studio's own
 * export). Case and trailing spaces do not matter. "Top 2 Box (Net)" and a question that merely
 * mentions a Top 2 Box are not summary headers.
 */
export function summaryBoxOfTitle(header: string): SummaryKind | null {
    for (const box of SUMMARY_KINDS) {
        if (SUMMARY_TITLE_RE[box].test(header)) return box;
    }
    return null;
}

/** True for a block that is a summary table (`Block.summary`), and never for a question's own. */
export function isSummaryBlock(block: { summary?: unknown }): boolean {
    return Boolean(block.summary);
}

/** What a reader keyed by question code needs to know of a block to tell an extra table. */
export interface ExtraTableMarks {
    summary?: unknown;
    /** M4S-5777: a ranked question's table (ranked.ts). Only the Rank 1 Summary is the question's own. */
    rank?: RankTable;
    /** M4S-5777 (X63): an allocation question's table (allocation.ts). The average table is its own. */
    allocation?: 'average' | 'distribution';
}

/**
 * True for a second table of a question that has its own block: a matrix's Top 2 / Bottom 2
 * summary (M4S-5592), or a ranked question's Rank 2… and Full Rank Summary tables (M4S-5777). A
 * ranked question's own block is its Rank 1 Summary, and is not extra.
 */
export function isExtraBlock(block: ExtraTableMarks): boolean {
    return (
        isSummaryBlock(block) || isExtraRankTable(block.rank) || block.allocation === 'distribution'
    );
}

/**
 * The blocks a reader that looks a question up by code should see: every block except the extra
 * tables (`isExtraBlock`). Order and identity are kept, so `withoutExtraBlocks(blocks)[i]` is the
 * same object as before.
 */
export function withoutExtraBlocks<T extends ExtraTableMarks>(blocks: readonly T[]): T[] {
    return blocks.filter((block) => !isExtraBlock(block));
}

/** The name the readers have always called; it leaves out every extra table, rank tables too. */
export const withoutSummaryBlocks = withoutExtraBlocks;

/**
 * True for a file header that names an extra table (`isExtraBlock`'s in a file): a box summary
 * (`summaryBoxOfTitle`) or a rank table other than the Rank 1 Summary (`rankTableOfTitle`). A
 * reader that keeps one table per question code skips everything up to the next header.
 */
export function isExtraTableTitle(header: string): boolean {
    return (
        summaryBoxOfTitle(header) !== null ||
        isExtraRankTable(rankTableOfTitle(header)) ||
        isAllocationDistributionTitle(header)
    );
}

/**
 * The tab a summary table gets in the one-tab-per-question export: "QCUSTOM2 T2B Summary", "QCUSTOM2
 * B2B Summary" or "QCUSTOM2 Avg Summary" (the Mean summary, M4S-5777 X60). The question's code is
 * cut to 15 characters so the name is at most 27, and a " (2)" suffix still fits Excel's 31. Statement tabs keep the names they always had ("QCUSTOM2", "QCUSTOM2
 * (2)" …); without these names the summaries would take those and push every statement tab along.
 */
export function summaryTabName(qid: string, box: SummaryKind): string {
    return `${qid.slice(0, 15).trimEnd()} ${SUMMARY_TAB_CODE[box]} Summary`;
}

/** A summary tab's short name per kind, three letters each, so a tab is at most 27. */
const SUMMARY_TAB_CODE: Record<SummaryKind, string> = { top: 'T2B', bottom: 'B2B', mean: 'Avg' };

const SUMMARY_TAB_RE = / (?:T2B|B2B|Avg) Summary(?: \(\d+\))?$/i;

/** True for a summary tab's name, with or without the " (n)" the workbook adds to repeats. */
export function isSummaryTabName(name: string): boolean {
    return SUMMARY_TAB_RE.test(name.trim());
}

/** What a name that reads as a summary tab is given after it. A " (2)" would not do: the pattern allows it. */
const PLAIN_TAB_SUFFIX = ' table';

/**
 * A sheet name no reader takes for a summary tab, cut to `max` characters. A crosstab or a custom
 * variable is named by the researcher, and one called "Logitech T2B Summary" would be skipped by
 * `dataSheetIndex` as a summary tab: the iOOH report builder read the cover sheet and found no
 * sections. Only a name that reads as a summary tab is changed ("Logitech T2B Summary table"); any
 * other comes back as it was. A real summary tab (`summaryTabName`) is never passed through here.
 */
export function plainTabName(name: string, max: number): string {
    if (!isSummaryTabName(name)) return name;
    return `${name.slice(0, max - PLAIN_TAB_SUFFIX.length).trimEnd()}${PLAIN_TAB_SUFFIX}`;
}

// ── The unweighted set (M4S-5777 X90) ─────────────────────────────────────────

/**
 * What every sheet of the unweighted set ends with, when an export carries the weighted and the
 * unweighted tables together. Written after the weighted sheets, so `dataSheetIndex` still finds a
 * weighted sheet first; a reader that walks every sheet skips these (`isUnweightedSheetName`), so
 * a question is never read twice.
 */
export const UNWEIGHTED_SHEET_SUFFIX = ' (unwtd)';

const UNWEIGHTED_SHEET_RE = / \(unwtd\)(?: \(\d+\))?$/i;

/** True for a sheet of the unweighted set, with or without the " (n)" the workbook adds to repeats. */
export function isUnweightedSheetName(name: string): boolean {
    return UNWEIGHTED_SHEET_RE.test(name.trim());
}

/** A sheet name with the unweighted suffix, cut so the whole stays within `max` characters. */
export function unweightedSheetName(name: string, max: number): string {
    return `${name.slice(0, max - UNWEIGHTED_SHEET_SUFFIX.length).trimEnd()}${UNWEIGHTED_SHEET_SUFFIX}`;
}
